<script setup lang="ts">
/**
 * Phone Input with country code selector.
 * Emits returnValue event in the format DynamicForm expects:
 * { value: string, error: boolean, index: number }
 */
interface Country {
  code: string;
  name: string;
  dial: string;
  placeholder: string;
}

interface Props {
  value?: string;
  // Optional id for the inner <input> so a page-level <label for="..."> keeps its
  // association (the checkout's delivery-contact rows and the app's account form
  // both label the field from outside the component).
  inputId?: string;
  disabled?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  title?: string;
  subtitle?: string;
  showTitle?: boolean;
  index?: number;
  width?: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: "",
  inputId: undefined,
  disabled: false,
  errorMessage: "",
  isRequired: false,
  title: "",
  subtitle: "",
  showTitle: true,
  index: 0,
  width: "100%",
});

const emit = defineEmits<{
  returnValue: [
    payload: {
      value: string;
      error: boolean;
      index: number;
    },
  ];
}>();

// Placeholders use x's instead of example digits so customers can't mistake
// them for a partially-filled value — see feedback from 2026-05-21. Spacing
// preserves each country's expected format so the field still hints at length.
const countries: Country[] = [
  { code: "GB", name: "United Kingdom", dial: "+44", placeholder: "xxxx xxxxxx" },
  { code: "US", name: "United States", dial: "+1", placeholder: "(xxx) xxx-xxxx" },
  { code: "IE", name: "Ireland", dial: "+353", placeholder: "xx xxx xxxx" },
  { code: "FR", name: "France", dial: "+33", placeholder: "x xx xx xx xx" },
  { code: "DE", name: "Germany", dial: "+49", placeholder: "xxx xxxxxxx" },
  { code: "ES", name: "Spain", dial: "+34", placeholder: "xxx xxx xxx" },
  { code: "IT", name: "Italy", dial: "+39", placeholder: "xxx xxx xxxx" },
  { code: "NL", name: "Netherlands", dial: "+31", placeholder: "x xxxxxxxx" },
  { code: "AU", name: "Australia", dial: "+61", placeholder: "xxx xxx xxx" },
  { code: "IN", name: "India", dial: "+91", placeholder: "xxxxx xxxxx" },
];

// Countries where the leading 0 is part of the national significant number, not a trunk
// prefix — so it must be kept in the international form (Italy: Rome "06…" stays "06…" in
// "+39 06…"). Everywhere we support (GB etc.) uses the 0 as a trunk prefix that's dropped.
const KEEP_LEADING_ZERO_DIALS = new Set(["+39"]);

const MAX_LENGTH = 30;
const MIN_DIGITS = 7;
const MAX_DIGITS = 15;
const ALLOWED_CHARACTERS = /^[+\d().\-\s]+$/;

function getFlagEmoji(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split("")
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join("");
}

const selectedCountry = ref<Country>(countries[0]);
const phoneNumber = ref("");
const showDropdown = ref(false);
const isValid = ref(true);
const dropdownRef = ref<HTMLElement | null>(null);

// Compares NORMALISED forms, not raw strings. The parent binds back the value this component
// emits, so a raw comparison saw "+44 7540313167" !== "+44 07540 313167" and reparsed the
// field mid-typing — the leading 0 vanished after the second keystroke and every space was
// eaten as it was typed. Comparing what the two actually mean leaves the customer's own
// formatting alone and only reparses a genuinely different number.
function isSameNumber(incoming: string): boolean {
  const trimmed = incoming.trim();
  const matched = countries
    .slice()
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((c) => trimmed.startsWith(c.dial));
  if (matched && matched.code !== selectedCountry.value.code) return false;
  const incomingLocal = matched ? trimmed.slice(matched.dial.length).trim() : trimmed;
  return nationalNumber(incomingLocal) === nationalNumber(phoneNumber.value);
}

watch(
  () => props.value,
  (to) => {
    if (to && !isSameNumber(to)) {
      // Parse incoming value to extract dial code and local number
      const trimmed = to.trim();
      const matchedCountry = countries
        .slice()
        .sort((a, b) => b.dial.length - a.dial.length)
        .find((c) => trimmed.startsWith(c.dial));
      if (matchedCountry) {
        selectedCountry.value = matchedCountry;
        phoneNumber.value = trimmed.slice(matchedCountry.dial.length).trim();
      } else {
        phoneNumber.value = trimmed;
      }
      // A value handed in by the parent (a prefill, or a cart restored on refresh) has to be
      // judged too — otherwise an invalid stored number shows no error and the page that
      // gates on this component's `error` never hears about it until the customer retypes.
      validate();
    }
  },
  { immediate: true },
);

function selectCountry(country: Country) {
  selectedCountry.value = country;
  showDropdown.value = false;
  validate();
}

function handleInput(e: Event) {
  const target = e.target as HTMLInputElement;
  phoneNumber.value = target.value;
  validate();
}

// Reduce whatever's in the field to the national significant number for the selected
// country, so prepending the dial code can't double it up. Handles a pasted E.164
// ("+447540313167"), a pasted country code without "+" ("447540313167") and a national
// trunk "0" ("07540313167") — all become "7540313167" for GB. Without this the field
// emitted "+44 +447540313167" / "+44 07540313167", which downstream (PedalMe) rejects.
function nationalNumber(input: string): string {
  let local = input.replace(/[\s\-()]/g, "").replace(/^\+/, "");
  const dialDigits = selectedCountry.value.dial.replace("+", "");
  if (local.startsWith(dialDigits)) local = local.slice(dialDigits.length);
  if (KEEP_LEADING_ZERO_DIALS.has(selectedCountry.value.dial)) return local;
  return local.replace(/^0+/, "");
}

// Validates the number we will actually EMIT — dial code included. Checking only the local
// part meant there was no upper bound at all: "+44 33333333333333333333333" passed the field
// silently and came back from the backend as "…is too long". Same rule as
// orderController's isInvalidCourierPhone (7–15 digits, E.164's cap) and the STRING(30)
// column, so anything this field accepts, the API accepts. This is the ONLY phone rule —
// consumers gate on the `error` this emits, they don't re-derive it from the string.
function isValidNumber(): boolean {
  const local = nationalNumber(phoneNumber.value);
  if (!local) return !props.isRequired;
  const full = `${selectedCountry.value.dial} ${local}`;
  const digits = full.replace(/\D/g, "");
  return (
    ALLOWED_CHARACTERS.test(phoneNumber.value) &&
    full.length <= MAX_LENGTH &&
    digits.length >= MIN_DIGITS &&
    digits.length <= MAX_DIGITS
  );
}

function validate() {
  isValid.value = isValidNumber();
  emitValue();
}

function emitValue() {
  const local = nationalNumber(phoneNumber.value);
  const fullNumber = local ? `${selectedCountry.value.dial} ${local}` : "";
  emit("returnValue", {
    value: fullNumber,
    error: !isValid.value,
    index: props.index,
  });
}

function handleBlur() {
  setTimeout(() => {
    showDropdown.value = false;
  }, 150);
}

function toggleDropdown() {
  if (!props.disabled) {
    showDropdown.value = !showDropdown.value;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

function handleClickOutside(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    showDropdown.value = false;
  }
}
</script>

<template>
  <div
    class="phone-input"
    :style="{ width }"
  >
    <span
      v-if="showTitle && title"
      class="text-container__title"
    >
      {{ title }}
      <i v-if="isRequired">*</i>
      <span
        v-if="subtitle"
        class="text-container__subtitle small-text"
      >
        {{ subtitle }}
      </span>
    </span>
    <div
      ref="dropdownRef"
      class="phone-input__row"
    >
      <div
        class="phone-input__country"
        @click="toggleDropdown"
      >
        <span class="phone-input__country--flag">{{ getFlagEmoji(selectedCountry.code) }}</span>
        <span class="phone-input__country--dial">{{ selectedCountry.dial }}</span>
        <fa
          class="phone-input__country--chevron"
          :icon="['fal', showDropdown ? 'chevron-up' : 'chevron-down']"
        />
      </div>
      <input
        :id="inputId"
        type="tel"
        :value="phoneNumber"
        :disabled="disabled"
        :required="isRequired"
        :placeholder="selectedCountry.placeholder"
        class="phone-input__field"
        autocomplete="tel"
        @input="handleInput"
        @blur="handleBlur"
      />
      <div
        v-if="showDropdown"
        class="phone-input__dropdown"
      >
        <div
          v-for="country in countries"
          :key="country.code"
          class="phone-input__dropdown--item"
          :class="{ active: selectedCountry.code === country.code }"
          @mousedown.prevent="selectCountry(country)"
        >
          <span class="flag">{{ getFlagEmoji(country.code) }}</span>
          <span class="name">{{ country.name }}</span>
          <span class="dial">{{ country.dial }}</span>
        </div>
      </div>
    </div>
    <BannerError
      :message="errorMessage || (!isValid && phoneNumber ? 'Please enter a valid phone number' : '')"
    />
  </div>
</template>

<style lang="scss">
.phone-input {
  margin-bottom: 1.6rem;

  .text-container__title {
    display: block;
    margin-bottom: 0.4rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--color-grey-1);
    i {
      color: var(--color-pink-1);
      font-style: normal;
    }
  }

  // Single bordered pill — border lives on the row, inner pieces are chrome-less
  // so the country block and tel input visually fuse into one control.
  &__row {
    display: flex;
    align-items: center;
    min-height: var(--tap);
    background-color: var(--color-white-1);
    border: 1px solid var(--color-grey-3);
    border-radius: var(--radius-sm);
    overflow: visible; // keep dropdown visible when open
    position: relative;
    transition:
      border-color var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out);
    &:focus-within {
      border-color: var(--color-pink-1);
      box-shadow: 0 0 0 3px var(--color-pink-3);
    }
  }

  &__country {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    padding: 0 0.6rem;
    font-size: 0.8rem;
    color: var(--color-grey-1);
    background: transparent;
    border-right: 1px solid var(--color-grey-3);
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    gap: 0.3rem;

    &--flag {
      font-size: 1.1rem;
    }

    &--dial {
      font-size: 0.8rem;
      color: var(--color-grey-1);
    }

    &--chevron {
      font-size: 0.5rem;
      color: var(--color-grey-2);
      margin-left: 0.15rem;
    }
  }

  // Hard reset of the global text-input chrome so the tel input is bare.
  &__field {
    flex: 1 1 auto;
    min-width: 0;
    font-size: var(--text-sm);
    color: var(--color-grey-1);
    font-family: inherit;
    line-height: 1.5 !important;
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0 0.8rem !important;
    min-height: auto !important;
    height: auto;

    &::placeholder {
      color: var(--color-grey-3);
      opacity: 1;
    }

    &:focus {
      outline: none !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    width: 100%;
    max-height: 14rem;
    overflow-y: auto;
    background-color: var(--color-white-1);
    border: 1px solid var(--color-grey-3);
    border-radius: var(--radius-sm);
    z-index: 9999;
    box-shadow: var(--shadow-2);
    &::-webkit-scrollbar {
      display: none;
    }

    &--item {
      display: flex;
      align-items: center;
      padding: 0.5rem 0.8rem;
      cursor: pointer;
      font-size: 0.85rem;
      gap: 0.5rem;

      .flag {
        font-size: 1.1rem;
      }

      .name {
        flex: 1;
        color: var(--color-grey-1);
      }

      .dial {
        color: var(--color-grey-2);
        font-size: 0.8rem;
      }

      &:hover {
        background-color: var(--color-pink-4);
      }

      &.active {
        background-color: var(--color-pink-4);
        font-weight: 500;
      }
    }
  }
}
</style>
